/**
 * The questionnaire in two parts (Michael, 2026-10-08, case imp_af790eed):
 *
 *   1. "Here is the information we have about you so far — please confirm or
 *      edit": every question the backend sealed WITH a value.
 *   2. "A few questions": what we don't have yet.
 *
 * …plus the benefits-card photo reader on part 2 whenever the plan identifiers
 * are among the empties (case imp_e5900eef): the reader already returns
 * level-tagged candidates (person / plan), so this is the mapping onto the
 * questionnaire's targets, mirroring `lib/insuranceConfirm.routeCandidates`
 * (which maps the same candidates onto PROFILE keys for the confirm step).
 *
 * Pure functions, no React, so they are unit-tested under node.
 */
import type { BridgeQuestion } from "../types/shared";

export type AnswerValue = string | string[];

export function isAnsweredValue(v: unknown): boolean {
  if (Array.isArray(v)) return v.length > 0;
  return v !== undefined && v !== null && String(v).trim() !== "";
}

/**
 * A question is "known" when the backend sealed a value for it — the chart
 * (or the physician) already answered it. It stays in the known section even
 * while the person edits it this session, so the page does not reshuffle
 * under their thumb.
 */
export function splitKnownAndToAnswer<Q extends { value?: unknown }>(
  questions: Q[],
): { known: Q[]; toAnswer: Q[] } {
  const known: Q[] = [];
  const toAnswer: Q[] = [];
  for (const q of questions) (isAnsweredValue(q.value) ? known : toAnswer).push(q);
  return { known, toAnswer };
}

// ── One question per screen (Michael, 2026-10-08) ────────────────────────────
//
// The unknowns are shown one at a time once there are more than three: big
// targets on a phone, "n of m", Back, and auto-advance when a choice is
// tapped. Below that a carousel adds screens without saving any effort, and
// the known-facts list above never becomes one — eleven known facts are
// faster to scan than to confirm one by one.

export const CAROUSEL_MIN_QUESTIONS = 4;

export function oneQuestionPerScreen(toAnswerCount: number): boolean {
  return toAnswerCount >= CAROUSEL_MIN_QUESTIONS;
}

/** Widgets whose single tap IS the answer, so the next question can follow. */
export const AUTO_ADVANCE_WIDGETS: ReadonlySet<string> = new Set(["choice", "yesno", "toggle"]);

/** Keep the step inside the list even when a conditional question appears or
 *  disappears under the person's thumb. */
export function clampStep(step: number, count: number): number {
  if (count <= 0) return 0;
  return Math.min(Math.max(0, Math.floor(step)), count - 1);
}

/** What the forward button says on the current screen. */
export function nextLabel(isLast: boolean, answered: boolean, required: boolean): string {
  if (isLast) return "Continue to review →";
  if (answered || required) return "Next →";
  return "Skip →";
}

// ── The known facts as a block, not one row under another ────────────────────
//
// "Here's what we have about you" is grouped into topical cards laid out in a
// grid (Michael, 2026-10-08): the plan, the person, how to reach them. Grouping
// is by the question's target name, because on a carrier form every box of the
// section shares one printed section title.

export interface KnownGroup<Q> {
  title: string;
  items: Q[];
}

const KNOWN_GROUPS: ReadonlyArray<[string, RegExp]> = [
  ["Your plan", /(contract|plan_(number|no|num|id)|policy|group_(number|no|num|id)|member_id|id_number|certificate|participant|sponsor|employer|language|insurer|carrier)/i],
  ["About you", /(first_name|last_name|full_name|_name$|date_of_birth|_dob\b|birth|gender|_sex\b|^sex)/i],
  ["How to reach you", /(address|street|suite|apartment|unit|city|province|postal|phone|telephone|mobile|email)/i],
];
const OTHER_GROUP = "Other details";

/** Which topical group a question belongs to, by its target name. Shared by
 *  the known-fact blocks and the plan holder's grouped question cards, so a
 *  fact and its question always sit under the same heading. */
export function factGroupTitle(target: unknown): string {
  const t = String(target || "");
  return KNOWN_GROUPS.find(([, re]) => re.test(t))?.[0] ?? OTHER_GROUP;
}

export function groupKnownQuestions<Q extends { target?: unknown }>(known: Q[]): KnownGroup<Q>[] {
  const buckets = new Map<string, Q[]>();
  for (const q of known) {
    const title = factGroupTitle(q.target);
    buckets.set(title, [...(buckets.get(title) || []), q]);
  }
  const order = [...KNOWN_GROUPS.map(([title]) => title), OTHER_GROUP];
  return order.filter((t) => buckets.has(t)).map((title) => ({ title, items: buckets.get(title) as Q[] }));
}

/** The value as a reader sees it: option labels for choices, Yes/No for toggles. */
export function displayValue(q: BridgeQuestion, v: AnswerValue): string {
  const opts = (q.options || []) as Array<{ target: string; label?: string }>;
  const labelOf = (t: unknown) => opts.find((o) => o.target === t)?.label ?? String(t);
  switch (q.widget) {
    case "yesno":
      return v === "yes" ? "Yes" : v === "no" ? "No" : v === "na" ? "Not applicable" : String(v ?? "");
    case "toggle":
      return v === "yes" || v === "✓" ? "Yes" : "No";
    case "choice":
      return labelOf(v);
    case "multi":
      return (Array.isArray(v) ? v : []).map(labelOf).join(", ");
    default:
      return Array.isArray(v) ? v.join(", ") : String(v ?? "");
  }
}

/**
 * What a known fact is called on its card. The question stem is written to be
 * ASKED ("What is your contract number? It's on your benefits card."), which
 * reads oddly above an answer we already have; the schema field's short label
 * ("Contract number") is what the review step shows too. Falls back to the
 * stem when no field matches or the field has no label.
 */
export function factLabel(
  question: { target?: unknown; label?: unknown },
  fields: ReadonlyArray<{ name?: unknown; label?: unknown }> | null | undefined,
): string {
  const target = String(question.target ?? "").toLowerCase();
  const field = target ? (fields || []).find((f) => String(f.name ?? "").toLowerCase() === target) : undefined;
  const label = field ? String(field.label ?? "").trim() : "";
  return label || String(question.label ?? "");
}

// Person-level first: "plan_member_id_number" contains "plan" but is the
// MEMBER's own identifier. Patterns follow the carriers' labels catalogued in
// insurer_registry (contract / plan / policy / group number at plan level;
// member ID / ID number / certificate / participant number at person level).
const PERSON_LEVEL = /(member_id|id_number|certificate|participant|member_number|subscriber_id)/i;
const PLAN_LEVEL = /(contract_(number|no|num)|plan_(number|no|num|id)|policy_(number|no|num)|group_(number|no|num|id)|plan_contract)/i;

export interface IdentifierTargets {
  plan?: string;
  person?: string;
}

/** The plan-identifier questions a benefits card can answer, by level. */
export function identifierTargets(questions: Array<{ target?: unknown }>): IdentifierTargets {
  const out: IdentifierTargets = {};
  for (const q of questions) {
    const t = String(q.target || "");
    if (!t) continue;
    if (!out.person && PERSON_LEVEL.test(t)) out.person = t;
    else if (!out.plan && PLAN_LEVEL.test(t)) out.plan = t;
  }
  return out;
}

export interface CardCandidate {
  field?: string;
  level?: "person" | "plan" | string;
  concept?: string;
  value?: unknown;
  confidence?: number;
}

/**
 * Route card OCR candidates onto the questionnaire's identifier targets.
 * Highest confidence wins a slot; a slot the person already answered is never
 * overwritten; 0.5 threshold. Legacy candidates with no level route by field
 * name (`group_id` → plan), as the profile-side router does.
 */
export function routeCardCandidatesToQuestions(
  candidates: CardCandidate[] | null | undefined,
  targets: IdentifierTargets,
  currentAnswers: Record<string, unknown>,
  threshold = 0.5,
): Record<string, string> {
  const patch: Record<string, string> = {};
  const ordered = [...(candidates || [])].sort((a, b) => (b.confidence || 0) - (a.confidence || 0));
  for (const c of ordered) {
    if (!c || c.confidence == null || c.confidence < threshold) continue;
    const value = String(c.value ?? "").trim();
    if (!value) continue;
    let target: string | undefined;
    if (c.level === "person") target = targets.person;
    else if (c.level === "plan") target = targets.plan;
    else if (c.field === "group_id" || c.field === "plan_number") target = targets.plan;
    else if (c.field === "insurance_id" || c.field === "member_id") target = targets.person;
    if (!target) continue;
    if (isAnsweredValue(currentAnswers[target])) continue;
    if (patch[target] !== undefined) continue; // a higher-confidence candidate already took it
    patch[target] = value;
  }
  return patch;
}
